import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-chat-1-line',
    title: 'On parle de votre projet',
    description:
      'On échange simplement sur vos besoins, vos envies et vos objectifs pour poser une base claire.',
  },
  {
    number: '02',
    icon: 'ri-pencil-ruler-2-line',
    title: 'Je crée votre site sur mesure',
    description:
      'Je conçois un design moderne et stratégique, pensé pour refléter votre image et attirer vos clients.',
  },
  {
    number: '03',
    icon: 'ri-checkbox-circle-line',
    title: "On ajuste ensemble",
    description:
      "Vous découvrez votre site, on peaufine les détails jusqu'à ce que tout soit parfait pour vous.",
  },
  {
    number: '04',
    icon: 'ri-rocket-line',
    title: 'Votre site est prêt',
    description:
      'Je mets votre site en ligne et vous accompagne pour une prise en main simple et rapide.',
  },
];

function useStepVisibility() {
  const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const elements = document.querySelectorAll('[data-step-index]');

    elements.forEach((el) => {
      const idx = el.getAttribute('data-step-index') || '';
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleMap((prev) => ({ ...prev, [idx]: true }));
            observer.disconnect();
          }
        },
        { threshold: 0.15 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return visibleMap;
}

export default function Process() {
  const sectionRef = useRef<HTMLElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const visibleMap = useStepVisibility();

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          headerObs.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const header = sectionRef.current.querySelector('[data-section-header]');
      if (header) headerObs.observe(header);
    }

    return () => {
      headerObs.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="process"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[900px] md:h-[900px] rounded-full bg-brand/[0.03] blur-[120px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          data-section-header
          className={`text-center mb-20 md:mb-28 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-brand mb-4 block">
            processus
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-display font-medium text-white mb-5 tracking-tight">
            Comment ça se passe ?
          </h2>
          <p className="text-white/40 max-w-lg mx-auto text-base md:text-lg font-light leading-relaxed">
            Un accompagnement clair, rapide et sans stress, du premier échange à la mise en ligne.
          </p>
        </div>

        {/* Timeline Steps */}
        <div className="relative">
          {/* Vertical timeline line — desktop centered, mobile left */}
          <div className="absolute left-6 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand/30 to-transparent" />

          <div className="space-y-16 md:space-y-24 relative">
            {steps.map((step, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleMap[index.toString()] || false;

              return (
                <div
                  key={step.number}
                  data-step-index={index}
                  className={`relative flex items-start md:items-center gap-8 md:gap-0 transition-all duration-1000 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                >
                  {/* Timeline dot + number badge */}
                  <div className="absolute left-6 md:left-1/2 md:-translate-x-1/2 top-0 md:top-1/2 md:-translate-y-1/2 z-10 flex flex-col items-center">
                    {/* Neon glow behind dot */}
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand/20 blur-md absolute" />
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-brand/40 bg-dark flex items-center justify-center relative">
                      <span className="text-brand text-[10px] md:text-xs font-bold tracking-wider">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Content card */}
                  <div
                    className={`ml-16 md:ml-0 md:w-1/2 ${
                      isLeft ? 'md:pr-16 md:text-right' : 'md:pl-16 md:ml-auto md:text-left'
                    }`}
                  >
                    <div
                      className={`group relative p-6 md:p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm transition-all duration-500 hover:border-brand/20 hover:bg-white/[0.04] ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                      }`}
                      style={{ transitionDelay: '150ms' }}
                    >
                      {/* Subtle top glow */}
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl" />

                      {/* Icon with glow */}
                      <div
                        className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-brand/[0.08] border border-brand/10 flex items-center justify-center mb-5 relative ${
                          isLeft ? 'md:ml-auto' : ''
                        }`}
                      >
                        <div className="absolute inset-0 rounded-xl bg-brand/10 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <i
                          className={`${step.icon} text-xl md:text-2xl text-brand relative`}
                        />
                      </div>

                      <h3 className="text-xl md:text-2xl font-display font-medium text-white mb-3 tracking-tight">
                        {step.title}
                      </h3>
                      <p className="text-sm md:text-base text-white/40 font-light leading-relaxed max-w-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}